/**
 * Attribution presets — DC-20 / USER_JOURNEYS §6 / contracts §7 + AM-7.
 *
 * Toggle state is DERIVED from `config.urlParams + serverData + stripeMappings`;
 * there is no new `WebPlacementConfig` schema field and the `sanitizeConfig`
 * allowlist (`src/modules/firebase-core/services/WebPlacementService.ts`) stays untouched.
 *
 * Row identity:
 *  - present         = same `key` + same `source` (Stripe mapping: `sourceType` + `sourceKey`)
 *  - preset-authored = present AND same `label` (the legacy "+ Facebook CAPI"/"+ TikTok Events API"
 *                      button rows match this — AC-067; a hand-picked autocomplete row matches
 *                      too, because it copies the same label — AC-038)
 *  - user-authored   = same key, different label → never removed (AC-051)
 *  - protectedKeys   = entryScreenRules[].paramKey ∪ variableBindings[].sourceKey → never removed (AC-051)
 *
 * `sharedUrlParams` (the `utm_*` rows) are additive: written when a network preset (facebook/tiktok)
 * is turned ON, NOT required for `deriveToggleState` to read that network preset as ON (a network
 * preset is "on" purely by its own `urlParams`/`serverData`/`stripeMappings`), and removed on OFF
 * only when no other ON preset (including the `utm` toggle itself) still declares them — contracts
 * §7 correction / AM-7: the `utm` toggle OWNS the `utm_*` rows; `gtm` is not a UTM source.
 *
 * OFF removes preset-authored rows that no other currently-ON preset declares (AC-023, AC-049),
 * except protected/user-authored rows, which are never removed (AC-051).
 */
import type {
  DestinationParameterMapping,
  ParameterDefinition,
  WebPlacementConfig,
} from '@modules/webplacements/types/webplacements.types';
import type { AnalyticsIntegration } from '@modules/analytics-settings/types/analytics.types';
import { REQUIRED_CONFIG_FIELDS, isEnabledAndConfigured } from '@modules/analytics-settings/config/requiredConfigFields';

export type AttributionPresetKey = 'facebook' | 'tiktok' | 'utm';
export const ATTRIBUTION_PRESET_KEYS: readonly AttributionPresetKey[] = ['facebook', 'tiktok', 'utm'];
export type AttributionToggleState = Record<AttributionPresetKey, boolean>;
export type AttributionConfigSlice = Pick<WebPlacementConfig, 'urlParams' | 'serverData' | 'stripeMappings'>;

export const ATTRIBUTION_NETWORK_LABELS: Record<AttributionPresetKey, string> = {
  facebook: 'Facebook',
  tiktok: 'TikTok',
  utm: 'UTM / Google',
};

export interface AttributionPreset {
  urlParams: ParameterDefinition[];
  serverData: ParameterDefinition[];
  stripeMappings: DestinationParameterMapping[];
  /** Additive: utm_* for network presets — written when ON, not required for the derived state. */
  sharedUrlParams: ParameterDefinition[];
}

const urlParam = (param: string, label: string): ParameterDefinition => ({
  key: param,
  source: 'url',
  sourceDetails: { urlParam: param },
  label,
  dataType: 'string',
});
const serverParam = (path: string, key: string, label: string): ParameterDefinition => ({
  key,
  source: 'server',
  sourceDetails: { serverPath: path },
  label,
  dataType: 'string',
});
const stripeMapping = (key: string): DestinationParameterMapping => ({ sourceType: 'server', sourceKey: key });

/**
 * Byte-identical to the rows the legacy "+ Facebook CAPI" button wrote
 * (`src/modules/webplacements/components/WebPlacementDialog.tsx:264-269`, AC-067).
 * Duplicated here (not imported) because the dialog does not export them and Task 15
 * rewires the dialog to consume this preset module — at that point the dialog's local
 * copy is deleted and this becomes the single source of truth.
 */
export const FACEBOOK_CAPI_DEFAULTS = [
  { path: 'ip', key: 'client_ip', label: 'IP Address' },
  { path: 'userAgent', key: 'client_user_agent', label: 'User Agent' },
  { path: 'facebook.fbp', key: 'facebook.fbp', label: 'Facebook Browser ID (fbp)' },
  { path: 'facebook.fbc', key: 'facebook.fbc', label: 'Facebook Click ID (fbc)' },
] as const;

/**
 * Byte-identical to the rows the legacy "+ TikTok Events API" button wrote
 * (`src/modules/webplacements/components/WebPlacementDialog.tsx:278-283`, AC-067).
 * Duplicated for the same reason as `FACEBOOK_CAPI_DEFAULTS` above.
 */
export const TIKTOK_EVENTS_API_DEFAULTS = [
  { path: 'ip', key: 'client_ip', label: 'IP Address' },
  { path: 'userAgent', key: 'client_user_agent', label: 'User Agent' },
  { path: 'tiktok.ttp', key: 'tiktok.ttp', label: 'TikTok Browser ID (ttp)' },
  { path: 'tiktok.ttclid', key: 'tiktok.ttclid', label: 'TikTok Click ID (ttclid)' },
] as const;

const UTM_ROWS: ParameterDefinition[] = [
  urlParam('utm_source', 'UTM Source'),
  urlParam('utm_medium', 'UTM Medium'),
  urlParam('utm_campaign', 'UTM Campaign'),
  urlParam('utm_content', 'UTM Content'),
  urlParam('utm_term', 'UTM Term'),
];

export const ATTRIBUTION_PRESETS: Record<AttributionPresetKey, AttributionPreset> = {
  facebook: {
    urlParams: [urlParam('fbclid', 'Facebook Click ID')],
    serverData: FACEBOOK_CAPI_DEFAULTS.map((d) => serverParam(d.path, d.key, d.label)),
    stripeMappings: FACEBOOK_CAPI_DEFAULTS.map((d) => stripeMapping(d.key)),
    sharedUrlParams: UTM_ROWS,
  },
  tiktok: {
    urlParams: [urlParam('ttclid', 'TikTok Click ID')],
    serverData: TIKTOK_EVENTS_API_DEFAULTS.map((d) => serverParam(d.path, d.key, d.label)),
    stripeMappings: TIKTOK_EVENTS_API_DEFAULTS.map((d) => stripeMapping(d.key)),
    sharedUrlParams: UTM_ROWS,
  },
  utm: {
    urlParams: [...UTM_ROWS, urlParam('gclid', 'Google Click ID')],
    serverData: [],
    stripeMappings: [],
    sharedUrlParams: [],
  },
};

// ── Stripe metadata budget ──
// System keys sourced VERBATIM from `web-shell/src/engine/v2/services/buildSystemStripeMetadata.ts:23-34`
// (also present compiled into `src/modules/builder-service/src/web-shell-ssr/ssr-entry.mjs` at the
// `buildSystemStripeMetadata` function) — the 11 Segmently-owned keys always reserved on a Stripe
// PaymentIntent regardless of any user/preset-authored metadata.
export const STRIPE_METADATA_KEY_CEILING = 50;
export const STRIPE_METADATA_VALUE_CEILING = 500;
export const SYSTEM_STRIPE_METADATA_KEYS: readonly string[] = [
  'analytics_consent',
  'journey_id',
  'analytics_session_id',
  'session_id',
  'project_id',
  'projectId',
  'onboarding_id',
  'onboardingId',
  'version_id',
  'versionId',
  'url_alias',
];

export class AttributionBudgetExceededError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AttributionBudgetExceededError';
  }
}

/** Union of Stripe metadata keys any attribution preset can write. */
export function presetStripeMetadataKeys(): string[] {
  const set = new Set<string>();
  ATTRIBUTION_PRESET_KEYS.forEach((k) => ATTRIBUTION_PRESETS[k].stripeMappings.forEach((m) => set.add(m.sourceKey)));
  return [...set];
}

// ── identity helpers ──
const sameRow = (a: ParameterDefinition, b: ParameterDefinition) => a.key === b.key && a.source === b.source;
const presetAuthored = (row: ParameterDefinition, preset: ParameterDefinition) =>
  sameRow(row, preset) && row.label === preset.label;
const sameMapping = (a: DestinationParameterMapping, b: DestinationParameterMapping) =>
  a.sourceType === b.sourceType && a.sourceKey === b.sourceKey;

const presetRows = (key: AttributionPresetKey) => ({
  url: [...ATTRIBUTION_PRESETS[key].urlParams, ...ATTRIBUTION_PRESETS[key].sharedUrlParams],
  server: ATTRIBUTION_PRESETS[key].serverData,
  stripe: ATTRIBUTION_PRESETS[key].stripeMappings,
});

/** ON iff every preset row (urlParams + serverData + stripeMappings — NOT sharedUrlParams) is present by key+source. */
export function deriveToggleState(config: AttributionConfigSlice): AttributionToggleState {
  const url = config.urlParams ?? [];
  const server = config.serverData ?? [];
  const stripe = config.stripeMappings ?? [];
  const isOn = (key: AttributionPresetKey) => {
    const preset = ATTRIBUTION_PRESETS[key];
    return (
      preset.urlParams.every((p) => url.some((r) => sameRow(r, p)))
      && preset.serverData.every((p) => server.some((r) => sameRow(r, p)))
      && preset.stripeMappings.every((p) => stripe.some((m) => sameMapping(m, p)))
    );
  };
  return { facebook: isOn('facebook'), tiktok: isOn('tiktok'), utm: isOn('utm') };
}

export function collectProtectedKeys(
  config: Pick<WebPlacementConfig, 'entryScreenRules' | 'variableBindings'>,
): Set<string> {
  const keys = new Set<string>();
  (config.entryScreenRules ?? []).forEach((rule) => keys.add(rule.paramKey));
  (config.variableBindings ?? []).forEach((binding) => keys.add(binding.sourceKey));
  return keys;
}

export type ProtectedReason = 'entry-rule' | 'variable-binding' | 'user-authored';

function turnOn(config: AttributionConfigSlice, key: AttributionPresetKey): AttributionConfigSlice {
  const rows = presetRows(key);
  const url = [...(config.urlParams ?? [])];
  const server = [...(config.serverData ?? [])];
  const stripe = [...(config.stripeMappings ?? [])];

  const missingStripe = rows.stripe.filter((p) => !stripe.some((m) => sameMapping(m, p)));
  if (stripe.length + missingStripe.length + SYSTEM_STRIPE_METADATA_KEYS.length > STRIPE_METADATA_KEY_CEILING) {
    throw new AttributionBudgetExceededError(
      `Turning ${ATTRIBUTION_NETWORK_LABELS[key]} attribution on would exceed Stripe's ${STRIPE_METADATA_KEY_CEILING} metadata-key ceiling `
      + `(${stripe.length} already mapped + ${SYSTEM_STRIPE_METADATA_KEYS.length} system keys + ${missingStripe.length} new). `
      + 'Remove a Stripe metadata row first.',
    );
  }

  rows.url.forEach((p) => { if (!url.some((r) => sameRow(r, p))) url.push({ ...p }); });
  rows.server.forEach((p) => { if (!server.some((r) => sameRow(r, p))) server.push({ ...p }); });
  missingStripe.forEach((p) => stripe.push({ ...p }));

  return { urlParams: url, serverData: server, stripeMappings: stripe };
}

function turnOff(
  config: AttributionConfigSlice,
  key: AttributionPresetKey,
  protectedKeys: Set<string>,
): AttributionConfigSlice {
  const state = deriveToggleState(config);
  const others = ATTRIBUTION_PRESET_KEYS.filter((k) => k !== key && state[k]);
  const neededUrl = new Set(others.flatMap((k) => presetRows(k).url.map((r) => r.key)));
  const neededServer = new Set(others.flatMap((k) => presetRows(k).server.map((r) => r.key)));
  const rows = presetRows(key);

  const url = (config.urlParams ?? []).filter((r) => {
    const preset = rows.url.find((p) => sameRow(r, p));
    if (!preset) return true;
    return !presetAuthored(r, preset) || protectedKeys.has(r.key) || neededUrl.has(r.key);
  });

  const removedServerKeys = new Set<string>();
  const server = (config.serverData ?? []).filter((r) => {
    const preset = rows.server.find((p) => sameRow(r, p));
    if (!preset) return true;
    const keep = !presetAuthored(r, preset) || protectedKeys.has(r.key) || neededServer.has(r.key);
    if (!keep) removedServerKeys.add(r.key);
    return keep;
  });

  const stripe = (config.stripeMappings ?? []).filter(
    (m) => !(m.sourceType === 'server' && removedServerKeys.has(m.sourceKey)),
  );

  return { urlParams: url, serverData: server, stripeMappings: stripe };
}

export function applyToggle(
  config: AttributionConfigSlice,
  key: AttributionPresetKey,
  on: boolean,
  protectedKeys: Set<string>,
): AttributionConfigSlice {
  return on ? turnOn(config, key) : turnOff(config, key, protectedKeys);
}

/** Seed helper: applies every ON toggle; a preset that would exceed the Stripe budget is skipped, never truncated. */
export function applyToggles(
  config: AttributionConfigSlice,
  toggles: AttributionToggleState,
  protectedKeys: Set<string> = new Set(),
): AttributionConfigSlice {
  return ATTRIBUTION_PRESET_KEYS.reduce((current, key) => {
    if (!toggles[key]) return current;
    try {
      return applyToggle(current, key, true, protectedKeys);
    } catch (error) {
      if (error instanceof AttributionBudgetExceededError) return current;
      throw error;
    }
  }, config);
}

/** Which of a preset's rows an OFF would have removed but kept instead, and why (AC-051). */
export function explainProtectedRows(
  config: AttributionConfigSlice & Pick<WebPlacementConfig, 'entryScreenRules' | 'variableBindings'>,
  key: AttributionPresetKey,
): Array<{ key: string; reason: ProtectedReason }> {
  const rows = presetRows(key);
  const entryRuleKeys = new Set((config.entryScreenRules ?? []).map((r) => r.paramKey));
  const bindingKeys = new Set((config.variableBindings ?? []).map((b) => b.sourceKey));
  const out: Array<{ key: string; reason: ProtectedReason }> = [];
  [...(config.urlParams ?? []), ...(config.serverData ?? [])].forEach((row) => {
    const preset = [...rows.url, ...rows.server].find((p) => sameRow(row, p));
    if (!preset) return;
    if (entryRuleKeys.has(row.key)) out.push({ key: row.key, reason: 'entry-rule' });
    else if (bindingKeys.has(row.key)) out.push({ key: row.key, reason: 'variable-binding' });
    else if (!presetAuthored(row, preset)) out.push({ key: row.key, reason: 'user-authored' });
  });
  return out;
}

const FACEBOOK_PLATFORMS = new Set<AnalyticsIntegration['platform']>(['facebook_pixel', 'facebook_capi']);
const TIKTOK_PLATFORMS = new Set<AnalyticsIntegration['platform']>(['tiktok_pixel', 'tiktok_events_api']);
// gtm is explicitly excluded — contracts §7 correction / AM-7: "gtm is not a UTM source".
const UTM_PLATFORMS = new Set<AnalyticsIntegration['platform']>([
  'segmently_internal', 'ga4', 'ga4_measurement_protocol', 'mixpanel', 'mixpanel_server',
  'amplitude', 'amplitude_server', 'posthog', 'posthog_server',
]);

/** Create-mode seed: ON iff an integration of that network is enabled AND configured (AC-021). */
export function defaultToggles(
  integrations: AnalyticsIntegration[],
  required: typeof REQUIRED_CONFIG_FIELDS = REQUIRED_CONFIG_FIELDS,
): AttributionToggleState {
  const live = integrations.filter((i) => isEnabledAndConfigured(i, required));
  const any = (set: Set<AnalyticsIntegration['platform']>) => live.some((i) => set.has(i.platform));
  return { facebook: any(FACEBOOK_PLATFORMS), tiktok: any(TIKTOK_PLATFORMS), utm: any(UTM_PLATFORMS) };
}
